import React, { useState, useRef, useEffect } from "react";
import { Card, Typography, Button, Flex, Divider } from "antd";
import {
  ExpandAltOutlined,
  ShrinkOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import "./index.scss";

const { Title, Text } = Typography;

interface CollapsibleInsightCardProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  expandedContent?: React.ReactNode;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  defaultExpanded?: boolean;
  themeColor?: string;
  /** Controlled mode: external expanded state */
  expanded?: boolean;
  /** Controlled mode: callback when expand/collapse is toggled */
  onExpandChange?: (expanded: boolean) => void;
}

const CollapsibleInsightCard: React.FC<CollapsibleInsightCardProps> = ({
  title,
  subtitle,
  icon,
  children,
  expandedContent,
  onRefresh,
  isRefreshing = false,
  defaultExpanded = false,
  themeColor,
  expanded,
  onExpandChange,
}) => {
  // Internal state for uncontrolled mode
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  
  // Use controlled or uncontrolled state
  const isControlled = expanded !== undefined;
  const isExpanded = isControlled ? expanded : internalExpanded;

  const [contentHeight, setContentHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  // Calculate content height for smooth animation
  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [expandedContent, isExpanded]);

  const toggleExpand = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newExpanded = !isExpanded;
    if (isControlled) {
      onExpandChange?.(newExpanded);
    } else {
      setInternalExpanded(newExpanded);
    }
  };

  return (
    <Card
      className={`collapsible-insight-card ${isExpanded ? "expanded" : "collapsed"}`}
      size="small"
    >
      {/* Header */}
      <Flex justify="space-between" align="flex-start" className="card-header">
        <Flex vertical gap={2}>
          <Title level={5} style={{ margin: 0 }}>
            {title}
          </Title>
        </Flex>
        {/* Refresh button with Last synced underneath */}
        {onRefresh && (
          <Flex vertical align="flex-end" gap={0}>
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined spin={isRefreshing} />}
              onClick={onRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? "Refreshing..." : "Refresh"}
            </Button>
            {subtitle && (
              <Text type="secondary" className="card-subtitle">
                {subtitle}
              </Text>
            )}
          </Flex>
        )}
      </Flex>

      {/* Summary Content (always visible) */}
      <div className="card-summary">{children}</div>

      {/* Expanded Content - Always rendered for smooth animation */}
      {expandedContent && (
        <div 
          className={`card-expanded-wrapper ${isExpanded ? "open" : "closed"}`}
          style={{ 
            maxHeight: isExpanded ? contentHeight + 32 : 0,
          }}
        >
          <div className="card-expanded-content" ref={contentRef}>
            {expandedContent}
          </div>
        </div>
      )}

      {/* Footer with Expand/Collapse button */}
      {expandedContent && (
        <>
          <Divider style={{ margin: "12px 0 8px 0" }} />
          <Flex justify="center" className="card-footer">
            <Button
              type="link"
              size="small"
              icon={isExpanded ? <ShrinkOutlined /> : <ExpandAltOutlined />}
              onClick={toggleExpand}
            >
              {isExpanded ? "Hide details" : "View details"}
            </Button>
          </Flex>
        </>
      )}
    </Card>
  );
};

export default CollapsibleInsightCard;
